import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TextareaModule } from 'primeng/textarea';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { CrmService, TransporterFleetOption } from '../../crm/crm.service';
import { RentalAgreement } from '../rental/rental.model';
import { RentalService } from '../rental/rental.service';
import { FleetOwnershipEligibility, FleetOwnershipTransferResource, FleetOwnershipTransferService, FleetOwnershipType } from './fleet-ownership-transfer.service';

export interface OwnershipTransferResource extends FleetOwnershipTransferResource {
    label: string;
}

@Component({
    selector: 'app-fleet-ownership-transfer-dialog',
    standalone: true,
    imports: [CommonModule, FormsModule, DialogModule, ButtonModule, SelectButtonModule, SelectModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule, TextareaModule],
    templateUrl: './fleet-ownership-transfer-dialog.component.html',
    styleUrls: ['./fleet-ownership-transfer-dialog.component.css']
})
export class FleetOwnershipTransferDialogComponent implements OnChanges {
    @Input() visible = false;
    @Input() owningCompanyId: number | null = null;
    @Input() resources: OwnershipTransferResource[] = [];
    @Input() pairedResource: OwnershipTransferResource | null = null;
    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() completed = new EventEmitter<number>();

    readonly ownershipOptions = [
        { label: 'Owned', value: 'OWNED' },
        { label: 'Freelance', value: 'FREELANCE' },
        { label: 'Rental', value: 'RENTAL' },
        { label: 'Transporter', value: 'TRANSPORTER' }
    ];
    targetOwnershipType: FleetOwnershipType = 'OWNED';
    targetCompanyId: number | null = null;
    destinationOptions: Array<{ id: number; name: string }> = [];
    loadingDestinations = false;
    destinationError = '';
    reason = '';
    rentalAgreementId: number | null = null;
    transporterCrmClientId: number | null = null;
    includePairedResource = false;
    rentalAgreements: RentalAgreement[] = [];
    transporterOptions: TransporterFleetOption[] = [];
    checking = false;
    saving = false;
    eligibility: FleetOwnershipEligibility | null = null;
    errorMessage = '';

    constructor(
        private ownershipTransfers: FleetOwnershipTransferService,
        private rentalService: RentalService,
        private crmService: CrmService
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible']?.currentValue === true) {
            this.reset();
            this.loadDestinations();
            this.loadTargetOptions();
        }
    }

    get selectedResources(): OwnershipTransferResource[] {
        const values = [...(this.resources || [])];
        if (this.includePairedResource && this.pairedResource) values.push(this.pairedResource);
        const unique = new Map<string, OwnershipTransferResource>();
        values.forEach(resource => unique.set(`${resource.resourceType}:${resource.resourceId}`, resource));
        return Array.from(unique.values());
    }

    get isRental(): boolean { return this.targetOwnershipType === 'RENTAL'; }
    get isTransporter(): boolean { return this.targetOwnershipType === 'TRANSPORTER'; }
    get readyToConfirm(): boolean { return !!this.eligibility && !this.errorMessage; }

    onChange(): void {
        this.eligibility = null;
        this.errorMessage = '';
    }

    onDestinationChange(): void {
        this.rentalAgreementId = null;
        this.transporterCrmClientId = null;
        this.onChange();
        this.loadTargetOptions();
    }

    checkEligibility(): void {
        if (!this.owningCompanyId || !this.targetCompanyId || !this.selectedResources.length) return;
        this.onChange();
        if (!this.validateDetails()) return;
        this.checking = true;
        this.ownershipTransfers.checkEligibility(this.owningCompanyId, this.selectedResources, this.targetCompanyId)
            .pipe(finalize(() => this.checking = false))
            .subscribe({
                next: response => {
                    if (response?.success) {
                        this.eligibility = response.data;
                    } else {
                        this.errorMessage = response?.message || 'The selected fleet resource cannot be transferred.';
                    }
                },
                error: error => this.errorMessage = error?.error?.message || 'Could not check active delivery eligibility.'
            });
    }

    submit(): void {
        if (!this.owningCompanyId || !this.targetCompanyId || !this.validateDetails() || !this.readyToConfirm) return;
        this.saving = true;
        this.ownershipTransfers.transfer({
            owningCompanyId: this.owningCompanyId,
            targetCompanyId: this.targetCompanyId,
            resources: this.selectedResources,
            targetOwnershipType: this.targetOwnershipType,
            reason: this.reason.trim(),
            rentalAgreementId: this.isRental ? this.rentalAgreementId : null,
            transporterCrmClientId: this.isTransporter ? this.transporterCrmClientId : null
        }).pipe(finalize(() => this.saving = false)).subscribe({
            next: response => {
                if (response?.success) {
                    this.completed.emit(this.targetCompanyId!);
                    this.close();
                } else {
                    this.eligibility = null;
                    this.errorMessage = response?.message || 'The ownership correction was not completed.';
                }
            },
            error: error => {
                this.eligibility = null;
                this.errorMessage = error?.error?.message || 'The ownership correction was not completed.';
            }
        });
    }

    close(): void {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    private validateDetails(): boolean {
        if (!this.targetCompanyId) {
            this.errorMessage = 'Select the destination company.';
            return false;
        }
        if (this.reason.trim().length < 3) {
            this.errorMessage = 'Enter a correction reason with at least 3 characters.';
            return false;
        }
        if (this.isRental && !this.rentalAgreementId) {
            this.errorMessage = 'Select the existing active rental agreement.';
            return false;
        }
        if (this.isTransporter && !this.transporterCrmClientId) {
            this.errorMessage = 'Select the CRM transporter.';
            return false;
        }
        return true;
    }

    private reset(): void {
        this.targetCompanyId = this.owningCompanyId;
        this.destinationOptions = [];
        this.destinationError = '';
        this.targetOwnershipType = 'OWNED';
        this.reason = '';
        this.rentalAgreementId = null;
        this.transporterCrmClientId = null;
        this.includePairedResource = false;
        this.eligibility = null;
        this.errorMessage = '';
    }

    private loadTargetOptions(): void {
        const companyId = this.targetCompanyId;
        this.rentalAgreements = [];
        this.transporterOptions = [];
        if (!companyId) return;
        forkJoin({
            agreements: this.rentalService.searchAgreements(companyId, 'ACTIVE').pipe(catchError(() => of({ items: [] } as any))),
            transporters: this.crmService.getScopedTransporters(companyId).pipe(catchError(() => of([])))
        }).subscribe(result => {
            if (this.targetCompanyId !== companyId) return;
            this.rentalAgreements = result.agreements?.items || [];
            this.transporterOptions = (result.transporters || []).filter(option => option.ownerCompanyId === companyId);
        });
    }

    private loadDestinations(): void {
        if (!this.owningCompanyId) return;
        this.loadingDestinations = true;
        this.ownershipTransfers.destinations(this.owningCompanyId)
            .pipe(finalize(() => this.loadingDestinations = false))
            .subscribe({
                next: options => this.destinationOptions = options,
                error: () => this.destinationError = 'Could not load eligible sister-company destinations.'
            });
    }
}
